import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { isSignInWithEmailLink } from 'firebase/auth';
import { db, ensureUser } from './firebase';
import { dayOneUnderway, isNewHere } from './lib/boot';
import { startPath } from './lib/starter';
import { clearImagineProgress } from './lib/imagine-progress';
import { dropPreDoor } from './lib/pre-door';
import { dropPreQuiz } from './lib/pre-quiz';
import { dropPreRoad } from './lib/pre-road';
import { Home } from './screens/Home';
import { Feed } from './screens/Feed';
import { Friends } from './screens/Friends';
import { Moment } from './screens/Moment';
import { adoptHandoffIfPresent, finishEmailSaveIfPresent, finishRedirectIfPresent, handoffInUrl } from './lib/account';
import { hydrateProfile, type HydrateMode } from './lib/profile-sync';
import { Exercise } from './screens/Exercise';
import { Closed } from './screens/Closed';
import { Intro } from './screens/Intro';
import { Warmup } from './screens/Warmup';
import { Imagine } from './screens/Imagine';
import { PutMeIn } from './screens/PutMeIn';
import { Reward } from './screens/Reward';
import { Story } from './screens/Story';
import { Print } from './screens/Print';
import { DevReveal } from './screens/DevReveal';
import { loadProfile, daysSinceMade, devProfileForDay, clearProfile } from './lib/state';
import { Path } from './screens/Path';
import { Film } from './screens/Film';
import { Map } from './screens/Map';
import { WelcomeBack } from './screens/WelcomeBack';
import { HandoffStuck } from './screens/HandoffStuck';
import { ChallengePage } from './screens/ChallengePage';
import { InvitePage } from './screens/InvitePage';
import { auth } from './firebase';
import { track } from './analytics';

/** `stuck`: a handoff the backend could not redeem (account.ts), so nobody new is made here. */
interface LiveConfig { open: boolean; closingMessage?: string; stuck?: boolean }

/**
 * How long boot may take before the app opens on what it already knows.
 *
 * Every step of boot is a network call, and **a promise that never settles is
 * not an error**: nothing throws, the catch below never runs, `cfg` stays null
 * and the app renders *nothing at all*. Someone behind a proxy, on a blocked
 * Firestore transport, or in a dead spot gets a permanently blank page — and
 * because no event is ever sent, it is invisible from this end too.
 *
 * It is not hypothetical. The Day 1 smoke test hits it whenever Firestore's
 * write channel is aborted: no console error, an empty `#root`, and a door that
 * never appears.
 *
 * On a timeout the app opens on what localStorage already has; the road merges
 * if the server answers afterwards. The kill switch fails open, which is what a
 * missing document and a thrown error already do.
 *
 * Except over a handoff link: a fresh browser has nothing in localStorage, so
 * opening early is opening on the intro, for somebody whose work is one redeem
 * away. The deadline starts once the redeem has answered (it bounds itself,
 * account.ts).
 */
const BOOT_DEADLINE_MS = 6000;

/** isNewHere, with what it needs read off this device. Local only: auth reads IndexedDB when nobody is stored. */
async function newHere(mode: HydrateMode): Promise<boolean> {
  const emailLink = isSignInWithEmailLink(auth, window.location.href);
  // anything cached already means waiting, so do not wait on auth to find that out
  if (!isNewHere({ mode, profile: loadProfile(), emailLink, signedIn: false })) return false;
  await auth.authStateReady();
  return isNewHere({ mode, profile: loadProfile(), emailLink, signedIn: !!auth.currentUser });
}

/**
 * Boot: the identity, any save or sign-in the URL is finishing, then the road
 * from the server (16 §4) alongside kindling-config/live, which carries the
 * kill switch (10 §14) and is public, so neither waits on the other. Missing
 * doc = open. The road is waited for, but not forever — and not at all by
 * somebody new on this device (isNewHere), who has no road to wait for.
 */
function useLiveConfig(mode: HydrateMode): LiveConfig | null {
  const [cfg, setCfg] = useState<LiveConfig | null>(null);
  useEffect(() => {
    let cancelled = false;
    let letIn = false; // a new person, shown the app before boot finished
    // How long the pulse was on screen, and how it ended: app_opened without a
    // boot_done is somebody who left a blank page.
    let reported = false;
    const done = (how: 'ok' | 'timeout' | 'error' | 'closed') => {
      if (reported) return;
      reported = true;
      track('boot_done', { how, ms: Math.round(performance.now()) });
    };
    const open = (why: string, how: 'timeout' | 'error') => {
      if (cancelled || letIn) return;
      console.warn(`[kindling] ${why}; running open`);
      done(how);
      setCfg({ open: true });
    };

    const boot = async (): Promise<LiveConfig | null> => {
      const params = new URLSearchParams(window.location.search);
      // DEV: /?newuser is a new person on a new phone: no cache, no identity
      if (params.has('newuser') && import.meta.env.DEV) { clearProfile(); await auth.signOut(); window.location.replace('/'); return null; }
      // A link from the save, opened in another browser than the one the work is
      // in: become that identity first, or this looks like somebody new and the
      // empty road below would be pushed over theirs. No code, no wait.
      const handoff = await adoptHandoffIfPresent().catch(() => 'none' as const);
      if (handoff === 'stuck') return { open: true, stuck: true };
      if (!timer) arm();
      if (handoff !== 'adopted' && await newHere(mode).catch(() => false)) {
        letIn = true;
        if (!cancelled) { done('ok'); setCfg({ open: true }); }
      }
      await ensureUser();
      await finishRedirectIfPresent();
      await finishEmailSaveIfPresent();
      // A brand-new identity has nothing on the server, so 'push' writes this
      // device's road up without reading first: a merge would read a copy from
      // before the person's first taps and could save it back over them.
      const [, snap] = await Promise.all([
        hydrateProfile(letIn ? 'push' : mode),
        getDoc(doc(db, 'kindling-config', 'live')),
      ]);
      const data = snap.exists() ? (snap.data() as LiveConfig) : { open: true };
      return { open: data.open !== false, closingMessage: data.closingMessage };
    };

    let timer = 0;
    const arm = () => { timer = window.setTimeout(() => open('boot did not finish in time', 'timeout'), BOOT_DEADLINE_MS); };
    if (!handoffInUrl()) arm();
    boot().then((c) => {
      window.clearTimeout(timer);
      if (cancelled || !c) return;
      // Already open for a new person: only a shut door is news, and setting
      // the same answer again would re-run the screen under them.
      if (letIn && c.open) return;
      // Still applied after a timeout has already opened the app: a late answer
      // saying the door is shut must close it.
      done(c.open ? 'ok' : 'closed');
      setCfg(c);
    }).catch((err) => {
      window.clearTimeout(timer);
      open(`config read failed (${(err as Error)?.message || err})`, 'error');
    });

    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [mode]);
  return cfg;
}

/**
 * The pulse index.html paints before any script runs, kept on screen while
 * boot runs (#115). React's first render replaces everything in #root, so
 * returning `null` here took the pulse away and left a blank page for as long
 * as boot took. Same id, so it is styled by the same rules in index.html.
 *
 * The replacement is a new element, and a new element starts its animation
 * from the first frame, which shows as a hitch mid-pulse. So it starts where
 * the painted one had got to: read once, while the painted one still exists.
 */
function BootPulse() {
  const [phase] = useState(() => {
    try {
      const t = document.querySelector('#boot i')?.getAnimations?.()[0]?.currentTime;
      return typeof t === 'number' ? t : 0;
    } catch { return 0; }
  });
  return <div id="boot" aria-hidden="true"><i style={phase ? { animationDelay: `-${Math.round(phase)}ms` } : undefined} /></div>;
}

/** A new person meets the intro and the Day 1 session; everyone else, Today. */
function FrontDoor() {
  // /?fresh has already reset the road (boot, mode 'reset'); keep ?door= so /?fresh&door=plush still skips the adult door
  const params = new URLSearchParams(window.location.search);
  if (params.has('fresh')) window.history.replaceState(null, '', params.has('door') ? `/?door=${encodeURIComponent(params.get('door') || '')}` : '/');
  const p = loadProfile();
  const intro = p.madeDays === 0 && !p.introSeen;
  // index.html drew the door from the cache it had; the road boot brought back says otherwise
  useEffect(() => { if (!intro) { dropPreDoor(); dropPreQuiz(); dropPreRoad(); } }, [intro]);
  // The page was loaded here (react-router's first location is keyed 'default';
  // every navigation after it gets its own key) mid-Day 1: back into the first
  // activity they were given — the warm-up, or the starter their ad chose
  // (lib/starter.ts), never the microphone for somebody the ad kept away from it.
  // The query is read off the window, not the router: dropDoorParam has taken `?d=` off since the router read it.
  const { key } = useLocation();
  if (intro) return <Intro />;
  if (key === 'default' && dayOneUnderway(p)) return <Navigate to={`${startPath()}${window.location.search}`} replace />;
  // A return after three or more days is the most celebrated moment in the app (R-08), once per return.
  const today = new Date().toISOString().slice(0, 10);
  if (p.madeDays > 0 && daysSinceMade(p) >= 3 && p.welcomedOn !== today) return <WelcomeBack />;
  return <Home />;
}

/**
 * `?d=<door>` has done its work by the time React runs: `initAnalytics()` read
 * it in main.tsx and wrote it into the first-touch campaign record, which is
 * where every later read comes from (17 §6.2). Take it off the url so a
 * reloaded, bookmarked or pasted link is not carrying a campaign parameter
 * around forever. Everything else in the query is left alone — `?fresh`,
 * `?door=plush` and `?day=N` are all read after this.
 */
function dropDoorParam() {
  const url = new URL(window.location.href);
  // `?start=` (lib/starter.ts) is the same kind of thing: read once into the campaign record, then done;
  // and `?idea_step=` / `?place_step=` / `?landing=`, the experiments' QA overrides, already kept by lib/experiments.ts
  const once = ['d', 'start', 'idea_step', 'place_step', 'landing'];
  if (!once.some((k) => url.searchParams.has(k))) return;
  for (const k of once) url.searchParams.delete(k);
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

export default function App() {
  useState(dropDoorParam);
  // DEV: /any/route?day=N opens that screen with N days made (see devProfileForDay), for looking at a section cold.
  // /?fresh walks Day 1 again, on this device and on the server.
  const [mode] = useState<HydrateMode>(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('fresh')) { clearProfile(); clearImagineProgress(); return 'reset'; }
    if (devProfileForDay(params)) { window.history.replaceState(null, '', window.location.pathname); return 'push'; }
    return 'merge';
  });
  const cfg = useLiveConfig(mode);
  // The door index.html drew sits over everything until AdultDoor claims it.
  // A shut app (or a stuck handoff) drops it at once; anything else that opened instead of the
  // door (FrontDoor drops it for Today itself) gets a moment, then it goes.
  useEffect(() => {
    if (!cfg) return;
    if (!cfg.open || cfg.stuck) { dropPreDoor(); dropPreQuiz(); dropPreRoad(); return; }
    const t = window.setTimeout(() => { dropPreDoor(); dropPreQuiz(); dropPreRoad(); }, 1500);
    return () => window.clearTimeout(t);
  }, [cfg]);
  if (!cfg) return <BootPulse />;
  if (!cfg.open) return <Closed message={cfg.closingMessage} />;
  if (cfg.stuck) return <HandoffStuck />;
  return (
    <Routes>
      <Route path="/" element={<FrontDoor />} />
      <Route path="/start" element={<Warmup />} />
      {/* the starter with no microphone, for ads that ask for it (?start=imagine, well/product/33) */}
      <Route path="/imagine" element={<Imagine />} />
      <Route path="/c/:code" element={<ChallengePage />} />
      <Route path="/i/:code" element={<InvitePage />} />
      <Route path="/feed" element={<Feed />} />
      <Route path="/friends" element={<Friends />} />
      <Route path="/path" element={<Path />} />
      <Route path="/film" element={<Film />} />
      {import.meta.env.DEV && <Route path="/map" element={<Map />} />}
      {import.meta.env.DEV && <Route path="/dev/reveal" element={<DevReveal />} />}
      <Route path="/moment" element={<Moment />} />
      <Route path="/next" element={<Reward />} />
      <Route path="/story/:postId" element={<Story />} />
      <Route path="/print/:postId" element={<Print />} />
      {/* Put Yourself In It is a starter with its own screen, reached from its road stop like any exercise */}
      <Route path="/do/put-yourself-in-it" element={<PutMeIn />} />
      <Route path="/do/:challengeId" element={<Exercise />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
